import { Card } from "@design";
import type { TFunction } from "i18next";
import { Telescope } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { Elapsed } from "./Elapsed";
import { ResearchCounts, ResearchTopics } from "./ResearchTopics";
import { ResearchTrail } from "./ResearchTrail";
import type { ResearchRunView } from "./researchModel";

/**
 * A research run while it streams (MHS-798): its stage (and round), its topics, what it has searched, found and read,
 * the newest search or page, and how long it's been going. Only the stage is announced: the counts change too often,
 * so the visible stage line (with how many topics it's searching or pages it has read) isn't the live region.
 */
export function ResearchProgress({ run, startedAt }: Readonly<{ run: ResearchRunView; startedAt?: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.progress" });
  const withRound = (stage: string) => (run.round > 1 ? t("stageRound", { stage, round: run.round }) : stage);
  const stage = t(`stage.${run.stage}`);
  return (
    <Card>
      <section aria-label={t("label")} className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Telescope size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />
          <output aria-live="polite" className="sr-only">
            {withRound(stage)}
          </output>
          <Text as="span" variant="body-sm" weight="semibold" className="min-w-0 flex-1" aria-hidden="true">
            {withRound(stageDetail(run, stage, t))}
          </Text>
          {startedAt && <Elapsed startedAt={startedAt} />}
        </div>
        <ResearchTopics topics={run.topics} />
        <ResearchCounts counts={run.counts} />
        <LatestLine run={run} />
        <ResearchTrail searches={run.searches} pages={run.pages} />
      </section>
    </Card>
  );
}

/** "Searching 3/7 topics" while it searches, "Reading 12 pages" once it reads; the stage alone otherwise. */
function stageDetail(run: ResearchRunView, stage: string, t: TFunction<"chat", "research.progress">): string {
  if (run.stage === "gathering") {
    const active = run.topics.filter((topic) => topic.status === "active").length;
    if (active > 0) return t("searchingTopics", { active, count: run.topics.length });
  }
  if (run.stage === "reading" && run.counts.pages > 0) return t("readingPages", { count: run.counts.pages });
  return stage;
}

const LATEST_KEYS = { query: "latestQuery", source: "latestSource", thought: "latestThought" } as const;

/**
 * The newest search or page; while the report is written, the writer's current step (or, from a model that doesn't
 * share its steps, what it's doing), since the last page it read is old news by then.
 */
function LatestLine({ run }: Readonly<{ run: ResearchRunView }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.progress" });
  const writing = run.stage === "writing" && run.latest?.kind !== "thought";
  if (!writing && !run.latest) return null;
  return (
    <Text variant="caption" tone="tertiary" className="truncate">
      {writing || !run.latest ? t("writingHint") : t(LATEST_KEYS[run.latest.kind], { text: run.latest.text })}
    </Text>
  );
}
